import { PromotionKind, formatMoney, type Promotion } from '@lombok/shared';
import type { BadgeTone } from '@lombok/ui';

/** Promotion-kind options for the create form. */
export const KIND_OPTIONS: { value: PromotionKind; label: string }[] = [
  { value: PromotionKind.PERCENTAGE, label: 'Descuento porcentual' },
  { value: PromotionKind.FIXED, label: 'Monto fijo (USD)' },
  { value: PromotionKind.VOLUME_REBATE, label: 'Rebate por volumen (%)' },
];

const KIND_LABELS: Record<string, string> = {
  [PromotionKind.PERCENTAGE]: 'Descuento %',
  [PromotionKind.FIXED]: 'Monto fijo',
  [PromotionKind.VOLUME_REBATE]: 'Rebate por volumen',
};

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

/** Human value: USD for fixed (value is minor units); a percent otherwise. */
export function promotionValueLabel(p: Pick<Promotion, 'kind' | 'value'>): string {
  if (p.kind === PromotionKind.FIXED) return `${formatMoney(p.value)} de descuento`;
  return `${p.value}%`;
}

export type PromotionStatus = 'scheduled' | 'active' | 'expired' | 'inactive' | 'archived';

/** Derive the live status from the active flag + the [startsAt, endsAt] window. */
export function promotionStatus(
  p: Pick<Promotion, 'active' | 'startsAt' | 'endsAt'> & { archived?: boolean },
  now = Date.now(),
): PromotionStatus {
  if (p.archived) return 'archived';
  if (!p.active) return 'inactive';
  if (now < new Date(p.startsAt).getTime()) return 'scheduled';
  if (now > new Date(p.endsAt).getTime()) return 'expired';
  return 'active';
}

/** Order of the status badges when sorting: live first, then upcoming, finished, switched off, archived. */
export const PROMOTION_STATUS_ORDER: readonly PromotionStatus[] = ['active', 'scheduled', 'expired', 'inactive', 'archived'];

export function promotionStatusLabel(s: PromotionStatus): string {
  switch (s) {
    case 'active':
      return 'Activa';
    case 'scheduled':
      return 'Programada';
    case 'expired':
      return 'Expirada';
    case 'inactive':
      return 'Inactiva';
    case 'archived':
      return 'Archivada';
  }
}

export function promotionStatusTone(s: PromotionStatus): BadgeTone {
  switch (s) {
    case 'active':
      return 'success';
    case 'scheduled':
      return 'info';
    case 'expired':
    case 'inactive':
    case 'archived':
      return 'neutral';
  }
}

/** Short date (no time) for the vigencia range. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
}
